import { callAI } from './ai.js';
import { getDueWords } from './leitner.js';
import { getHeroName, getAgeBracketDesc } from './tracker.js';
import { CHILD_INTERESTS } from '../constants/themes.js';
import { LEVELS } from '../constants/levels.js';
import { todayStr } from './utils.js';

export function getAppTier() {
  try { return localStorage.getItem('rq:tier') || 'trial'; } catch { return 'trial'; }
}

export function canAccessStories() {
  const t = getAppTier();
  return t === 'plus' || t === 'family' || t === 'trial';
}

export const WELCOME_STORY = (name) => ({
  title: `The Day Finley Met ${name}`,
  story: `One evening, as the sun painted the sky orange and pink, a cheerful blue dolphin named Finley was swimming near the shore when a message in a bottle floated by. Inside was a note with just one word: "${name}."\n\n"That's a wonderful name," Finley said with a happy leap. Using magic dolphin speed, Finley dove through sparkling waves to find you.\n\nWhen Finley arrived, there was ${name} — ready for adventure! "I've been looking for you," Finley said with a big smile. "I've been saving the very best stories just for you."\n\n${name} climbed onto Finley's back and together they soared through the moonlit ocean. Stars appeared one by one, like tiny lighthouses in the sky.\n\n"Every night," Finley whispered as ${name} felt warm and sleepy, "I'll come back with a new adventure — just for you." The gentle waves rocked them softly as the first stars came out to say goodnight.\n\nAnd somewhere beneath the sleeping sea, Finley was already dreaming up tomorrow's tale...`,
  seriesHook: `Finley and ${name} had just met under the evening stars, and Finley had promised a new adventure every night.`,
  vocabularyUsed: [],
});

export async function generateBedtimeStory(profile, tracker, interests) {
  const childId = profile.id;
  const age = profile.age || 7;
  const heroName = getHeroName(profile);
  const ageBracket = getAgeBracketDesc(profile);
  const lv = tracker.currentLevel;
  const ehri = LEVELS[lv]?.ehri || 'Full alphabetic';
  const intLabels = (interests || []).map(id => { const ci = CHILD_INTERESTS.find(x => x.id === id); return ci ? ci.label : id; }).filter(Boolean);
  const dueWords = getDueWords(tracker.wordMastery, age, 5);
  let seriesHook = null;
  try { const sd = JSON.parse(localStorage.getItem(`rq:series:${childId}`) || 'null'); seriesHook = sd?.hook || null; } catch {}
  const vocabStr = dueWords.length > 0 ? dueWords.join(', ') : 'journey, wonder, brave, kind, explore';
  const interestStr = intLabels.length > 0 ? intLabels.join(', ') : 'adventure and discovery';
  const hookStr = seriesHook || 'This is the first story in a new adventure.';
  const prompt = `Write a 400-500 word bedtime story for a ${ageBracket} named ${heroName} who loves ${interestStr}. Reading level: ${ehri}. Weave in these vocabulary words naturally: ${vocabStr}. Include ${heroName} as the main character. End with a gentle cliffhanger for tomorrow's chapter. The story should be calming and appropriate for bedtime. Previous chapter ended with: ${hookStr}. Return ONLY valid JSON: {"title":"...","story":"...","seriesHook":"last 2 sentences as hook for next chapter","vocabularyUsed":["word1"]}`;
  const result = await callAI(prompt);
  if (!result || typeof result !== 'object' || !result.story) throw new Error('Invalid story response');
  const today = todayStr();
  try {
    localStorage.setItem(`rq:story:${childId}:${today}`, JSON.stringify({...result, date: today, read: false}));
    localStorage.setItem(`rq:series:${childId}`, JSON.stringify({hook: result.seriesHook, name: result.title}));
  } catch {}
  return result;
}

export function getTodayStory(childId) {
  try { const v = localStorage.getItem(`rq:story:${childId}:${todayStr()}`); return v ? JSON.parse(v) : null; } catch { return null; }
}

export function markStoryRead(childId) {
  const key = `rq:story:${childId}:${todayStr()}`;
  try { const v = localStorage.getItem(key); if (v) { const s = JSON.parse(v); localStorage.setItem(key, JSON.stringify({...s, read: true})); } } catch {}
}

export function getRecentStories(childId) {
  const stories = []; const today = new Date();
  for (let i = 1; i < 7; i++) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const ds = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    try { const v = localStorage.getItem(`rq:story:${childId}:${ds}`); if (v) stories.push({...JSON.parse(v), date: ds, chapter: i}); } catch {}
  }
  return stories;
}
